import { useState, useEffect, useRef } from "react"
import { X, Mail, ChevronDown, Search } from "lucide-react"

const ff = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const divider = "1px solid #f3f4f6"

const ARCHIVE_REASONS = [
  "Better for another role",
  "Comp",
  "Future hire",
  "No Response",
  "Offer declined",
  "Position closed",
  "Position filled",
  "Timing",
  "Under-qualified",
  "Withdrew",
]

const useOutsideClick = (cb) => {
  const ref = useRef(null)
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) cb() }
    document.addEventListener("mousedown", h)
    return () => document.removeEventListener("mousedown", h)
  }, [cb])
  return ref
}

const ArchiveLeadModal = ({ selectedIds, jobListing, onClose, onArchive }) => {
  const [reason, setReason] = useState("")
  const [reasonSearch, setReasonSearch] = useState("")
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [sendEmail, setSendEmail] = useState(false)
  const [visible, setVisible] = useState(false)

  const searchRef = useRef(null)
  const dropdownRef = useOutsideClick(() => {
    setDropdownOpen(false)
    setReasonSearch("")
  })

  const count = selectedIds.size

  // Fade-in on mount — mirrors EmailModal's visible state pattern
  useEffect(() => {
    const t = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(t)
  }, [])

  useEffect(() => {
    if (dropdownOpen) setTimeout(() => searchRef.current?.focus(), 50)
  }, [dropdownOpen])

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (dropdownOpen) {
          setDropdownOpen(false)
          setReasonSearch("")
          return
        }
        handleClose()
      }
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [dropdownOpen])

  const handleClose = () => {
    setVisible(false)
    setTimeout(onClose, 200)
  }

  const handleArchive = () => {
    onArchive({ selectedIds, reason, sendEmail })
  }

  const filteredReasons = ARCHIVE_REASONS.filter((r) =>
    r.toLowerCase().includes(reasonSearch.toLowerCase()),
  )

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        .arch-reason-btn:hover { background: #f9fafb !important; }
        .arch-close-btn:hover { color: #374151 !important; background: #f3f4f6 !important; }
        .arch-cancel-btn:hover { border-color: #9ca3af !important; color: #111827 !important; }
        .arch-archive-btn-active:hover { background: #1d4ed8 !important; }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={handleClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 200,
          background: "rgba(0,0,0,0.28)",
          opacity: visible ? 1 : 0,
          transition: "opacity 0.18s ease",
        }}
      />

      {/* Positioning wrapper — top-aligned like your request, matches EmailModal structure */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 201,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          padding: "64px 16px 24px",
          pointerEvents: "none",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "100%",
            maxWidth: 520,
            background: "#fff",
            borderRadius: 10,
            border: "1px solid #e5e7eb",
            boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
            fontFamily: ff,
            pointerEvents: "all",
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0) scale(1)" : "translateY(6px) scale(0.99)",
            transition: "opacity 0.18s ease, transform 0.18s cubic-bezier(0.22,1,0.36,1)",
            overflow: "visible",
          }}
        >
          {/* ── Header ── */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              padding: "16px 20px",
              borderBottom: divider,
            }}
          >
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: "#111827", margin: 0, letterSpacing: "-0.01em" }}>
                Archive leads
              </p>
              {jobListing?.role && (
                <p style={{ fontSize: 13, color: "#374151", margin: "3px 0 0", fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: "#34d399",
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontWeight: 600, color: "#111827" }}>{jobListing.role}</span>
                  {[jobListing?.location, jobListing?.department, jobListing?.category].filter(Boolean).length > 0 && (
                    <>
                      <span style={{ color: "#d1d5db" }}>·</span>
                      <span style={{ color: "#6b7280", fontWeight: 500 }}>
                        {[jobListing?.location, jobListing?.department, jobListing?.category].filter(Boolean).join(", ")}
                      </span>
                    </>
                  )}
                </p>
              )}
            </div>

            <button
              onClick={handleClose}
              className="arch-close-btn"
              style={{
                width: 28, height: 28,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "none", border: "none", borderRadius: 6,
                cursor: "pointer", color: "#6b7280",
                transition: "all 0.12s", flexShrink: 0,
              }}
            >
              <X size={14} strokeWidth={2} />
            </button>
          </div>

          {/* ── Archive reason row ── */}
          <div style={{ padding: "14px 20px", borderBottom: divider }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 8px" }}>
              Archive reason
            </p>

            <div ref={dropdownRef} style={{ position: "relative" }}>
              <button
                type="button"
                onClick={() => setDropdownOpen((v) => !v)}
                style={{
                  width: "100%",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  height: 38, padding: "0 12px",
                  border: dropdownOpen ? "1px solid #9ca3af" : "1px solid #e5e7eb",
                  borderRadius: 7,
                  background: "#fafafa",
                  fontSize: 13.5, fontWeight: reason ? 600 : 500,
                  color: reason ? "#111827" : "#9ca3af",
                  cursor: "pointer", fontFamily: ff,
                  transition: "border-color 0.12s",
                  outline: "none",
                }}
                onMouseEnter={(e) => { if (!dropdownOpen) e.currentTarget.style.borderColor = "#d1d5db" }}
                onMouseLeave={(e) => { if (!dropdownOpen) e.currentTarget.style.borderColor = "#e5e7eb" }}
              >
                {reason || "Choose archive reason"}
                <ChevronDown
                  size={13}
                  strokeWidth={2.5}
                  style={{
                    color: "#9ca3af", flexShrink: 0, marginLeft: 6,
                    transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.15s",
                  }}
                />
              </button>

              {dropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0, right: 0,
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    zIndex: 300,
                    overflow: "hidden",
                  }}
                >
                  {/* Search inside dropdown */}
                  <div style={{ padding: "8px 10px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", gap: 7 }}>
                    <Search size={12} strokeWidth={2} style={{ color: "#9ca3af", flexShrink: 0 }} />
                    <input
                      ref={searchRef}
                      type="text"
                      placeholder="Search..."
                      value={reasonSearch}
                      onChange={(e) => setReasonSearch(e.target.value)}
                      style={{
                        flex: 1, border: "none", outline: "none",
                        fontSize: 13, color: "#111827", fontFamily: ff,
                        background: "transparent", fontWeight: 500,
                      }}
                    />
                  </div>

                  <div style={{ maxHeight: 220, overflowY: "auto" }}>
                    {filteredReasons.length === 0 ? (
                      <p style={{ padding: "10px 14px", fontSize: 13, color: "#9ca3af", fontWeight: 500 }}>No results</p>
                    ) : (
                      filteredReasons.map((r) => (
                        <button
                          key={r}
                          type="button"
                          className="arch-reason-btn"
                          onClick={() => {
                            setReason(r)
                            setDropdownOpen(false)
                            setReasonSearch("")
                          }}
                          style={{
                            display: "block", width: "100%", textAlign: "left",
                            padding: "9px 14px",
                            border: "none", borderBottom: "1px solid #f9fafb",
                            background: reason === r ? "#f0f9ff" : "none",
                            fontSize: 13.5, fontWeight: reason === r ? 600 : 500,
                            color: reason === r ? "#0369a1" : "#374151",
                            cursor: "pointer", fontFamily: ff,
                            transition: "background 0.1s",
                          }}
                        >
                          {r}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Send email checkbox — matches EmailModal's exclude checkbox row ── */}
          <div
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 20px", borderBottom: divider }}
          >
            <input
              type="checkbox"
              id="arch-send-email"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              style={{ width: 14, height: 14, cursor: "pointer", accentColor: "#111827", flexShrink: 0 }}
            />
            <Mail size={13} style={{ color: "#6b7280", flexShrink: 0 }} strokeWidth={2} />
            <label
              htmlFor="arch-send-email"
              style={{
                fontSize: 13, color: "#374151", cursor: "pointer",
                fontWeight: 500, userSelect: "none",
              }}
            >
              Send email to candidates?
            </label>
          </div>

          {/* ── Footer ── */}
          <div
            style={{
              display: "flex", alignItems: "center", justifyContent: "flex-end",
              gap: 10, padding: "12px 20px",
            }}
          >
            <button
              type="button"
              onClick={handleClose}
              className="arch-cancel-btn"
              style={{
                background: "none", border: "1px solid #e5e7eb", borderRadius: 7,
                padding: "7px 16px", fontSize: 13, fontWeight: 600, color: "#374151",
                cursor: "pointer", fontFamily: ff, transition: "all 0.12s",
              }}
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleArchive}
              disabled={!reason}
              className={reason ? "arch-archive-btn-active" : ""}
              style={{
                height: 36, padding: "0 20px",
                background: reason ? "#3b82f6" : "#f3f4f6",
                border: "none", borderRadius: 7,
                fontSize: 13, fontWeight: 600,
                color: reason ? "#fff" : "#9ca3af",
                cursor: reason ? "pointer" : "not-allowed",
                fontFamily: ff, letterSpacing: "-0.01em",
                transition: "background 0.12s, color 0.12s",
              }}
            >
              Archive ({count})
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default ArchiveLeadModal